/**
 * CreatorStorePage — `/store/:creator`. Public, no auth, no shell.
 *
 * Lives outside `ProtectedRoute` so it shares like a marketing page —
 * paste the URL on Twitter, LinkedIn, or in a DM and the recipient
 * sees the creator's full catalogue without a sign-up wall.
 *
 * Currently dormant: no creator profiles are registered. Every slug
 * bounces back to /marketplace. The page is preserved so the route
 * stays valid for when the per-creator storefront API ships.
 */

import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GiOrbitLogo } from '@/components/GiOrbitLogo';
import { DomainBadge } from '@/components/Badge';
import { DomainIcon } from '@/components/DomainIcon';
import { Button } from '@/components/Button';
import type { MarketplaceListing } from '@/api/types';
import { useLocaleStore } from '@/stores/localeStore';
import { formatNumber, normalizeDomain } from '@/utils/format';

interface CreatorProfile {
  slug: string;
  name: string;
  bio_en: string;
  bio_he: string;
  initial: string;
  accent: string;
  /** Visible banner — links to the creator's external presence; UI
   *  only renders the label, not the URL bar. */
  external_label: string;
  external_href: string;
  joined_at: string; // ISO 8601
}

// Creator program is currently dormant. The page is preserved so the
// route stays valid; with no creator profiles registered, every slug
// bounces back to /marketplace.
const CREATORS: Record<string, CreatorProfile> = {};

/** Mock earnings until the marketplace's per-creator settlement API
 *  lands (Phase 4.9). Real numbers will flow from the same sidecar
 *  that powers the EarningsDashboardPage chart. */
function mockEarnings(productCount: number): {
  total_usd: number;
  this_month_usd: number;
  forks_total_usd: number;
  paying_customers: number;
} {
  // Deterministic pseudo-randomness from `productCount` so the
  // numbers stay stable across renders without a real backend.
  const seed = productCount * 1_249;
  return {
    total_usd: 18_240 + (seed % 6_000),
    this_month_usd: 2_104 + (seed % 850),
    forks_total_usd: 1_640 + (seed % 540),
    paying_customers: 47 + (seed % 19),
  };
}

export function CreatorStorePage() {
  const { creator: creatorSlug = '' } = useParams<{ creator: string }>();
  const { t } = useTranslation(['marketplace', 'common']);
  const locale = useLocaleStore((s) => s.locale);
  const [copied, setCopied] = useState(false);

  const creator = CREATORS[creatorSlug.toLowerCase()];
  // Creator catalogues are out — feature kept on ice until a real
  // creator program is opened. Every recognised creator slug shows an
  // empty catalogue today.
  const listings = useMemo<MarketplaceListing[]>(() => [], []);
  const earnings = useMemo(
    () => mockEarnings(listings.length),
    [listings.length],
  );

  if (!creator) {
    // Unknown slug — bounce to the public marketplace landing.
    return <Navigate to="/marketplace" replace />;
  }

  const bio = locale === 'he' ? creator.bio_he : creator.bio_en;
  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/store/${creator.slug}`
      : `https://app.growing-intelligence.com/store/${creator.slug}`;

  const onShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      /* clipboard unavailable — silently fall back to no-op */
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-ink">
      <main className="mx-auto flex max-w-5xl flex-col gap-8 px-5 py-10">
        {/* Top bar — minimal, no shell */}
        <div className="flex items-center justify-between text-sm">
          <Link to="/" className="flex items-center gap-2 text-ink-muted hover:text-ink">
            <GiOrbitLogo size="sm" />
            <span className="font-semibold">Growing Intelligence</span>
          </Link>
          <Link to="/marketplace" className="text-ink-muted hover:text-ink">
            {t('detail.back')}
          </Link>
        </div>

        {/* Creator hero */}
        <header className="flex flex-col gap-5 rounded-2xl border border-border bg-surface p-6 md:flex-row md:items-start md:gap-6">
          <div
            className="flex h-20 w-20 flex-none items-center justify-center rounded-full text-3xl font-bold text-white"
            style={{ backgroundColor: creator.accent }}
            aria-hidden
          >
            {creator.initial}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{creator.name}</h1>
              <span
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                style={{ backgroundColor: '#eef2ff', color: creator.accent }}
              >
                Independent creator
              </span>
            </div>
            <p className="max-w-prose text-sm text-ink-muted">{bio}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-ink-subtle">
              <a
                href={creator.external_href}
                target="_blank"
                rel="noreferrer noopener"
                className="font-medium hover:underline"
                style={{ color: creator.accent }}
              >
                {creator.external_label}
              </a>
              <span>{listings.length} products in catalogue</span>
              <span>
                Joined{' '}
                {new Date(creator.joined_at).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'short',
                })}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-stretch gap-2 md:items-end">
            <Button
              variant={copied ? 'secondary' : 'primary'}
              onClick={() => void onShare()}
              size="sm"
            >
              {copied ? 'Copied!' : 'Copy share link'}
            </Button>
            <span className="font-mono text-[10px] text-ink-subtle">
              /store/{creator.slug}
            </span>
          </div>
        </header>

        {/* Earnings strip — mock until the settlement API lands. */}
        <section
          aria-label="Creator earnings summary"
          className="grid grid-cols-2 gap-3 rounded-2xl border border-border bg-surface p-4 sm:grid-cols-4"
        >
          <Stat label="Total revenue" value={`$${formatNumber(earnings.total_usd, locale)}`} />
          <Stat label="This month" value={`$${formatNumber(earnings.this_month_usd, locale)}`} />
          <Stat
            label="Fork royalties"
            value={`$${formatNumber(earnings.forks_total_usd, locale)}`}
          />
          <Stat
            label="Paying customers"
            value={formatNumber(earnings.paying_customers, locale)}
          />
        </section>

        {/* Catalogue */}
        <section aria-labelledby="catalogue-heading" className="flex flex-col gap-4">
          <div className="flex items-baseline justify-between border-b border-border pb-2">
            <h2 id="catalogue-heading" className="text-lg font-semibold">
              Catalogue
            </h2>
            <span className="text-xs text-ink-subtle">{listings.length} products</span>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {listings.map((l) => {
              const normalized = normalizeDomain(l.domain);
              const domainLabel =
                typeof l.domain === 'string' ? l.domain : l.domain.Custom;
              return (
                <Link
                  key={l.root_id}
                  to={`/marketplace/${l.root_id}`}
                  className="group flex flex-col gap-3 rounded-xl border border-border bg-surface p-5 transition-shadow hover:shadow-raised"
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-domain-${normalized}-soft text-domain-${normalized}`}
                    >
                      <DomainIcon domain={normalized} className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <DomainBadge domain={normalized} label={domainLabel} />
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{ backgroundColor: '#eef2ff', color: creator.accent }}
                        >
                          {creator.name}
                        </span>
                      </div>
                      <h3 className="mt-1 text-base font-semibold tracking-tight text-ink group-hover:text-spark">
                        {l.name}
                      </h3>
                      <p className="mt-0.5 text-sm text-ink-muted">{l.tagline}</p>
                    </div>
                  </div>
                  <div className="mt-auto flex items-center justify-between text-xs text-ink-subtle">
                    <span>v{l.version}</span>
                    <span className="font-semibold text-ink">Contact Us</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <footer className="pt-6 text-center text-xs text-ink-subtle">
          <p>
            Want a creator storefront like this?{' '}
            <Link
              to="/spark"
              className="font-medium hover:underline"
              style={{ color: creator.accent }}
            >
              Sign your first GI
            </Link>
            .
          </p>
        </footer>
      </main>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
        {label}
      </span>
      <span className="text-xl font-semibold text-ink">{value}</span>
    </div>
  );
}
